import React from 'react';
import { Box, styled } from '@mui/material';
import { useResponsiveLayout } from '../../hooks/useResponsiveLayout';

interface FourPaneLayoutProps {
  navigationPane: React.ReactNode;  // 左端: ナビゲーション・カテゴリー
  inboxPane: React.ReactNode;       // 左中: チャット一覧
  chatPane: React.ReactNode;        // 右中: チャット内容（メイン）
  profilePane: React.ReactNode;     // 右端: 顧客プロファイル
  navigationWidth?: number;
  inboxWidth?: number;
  profileWidth?: number;
}

const Container = styled(Box)(({ theme }) => ({
  display: 'flex',
  height: '100%', // 親要素に依存させてヘッダー分を除外
  width: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
}));

const NavigationPane = styled(Box)<{ width: number; show: boolean }>(({ width, show, theme }) => ({
  width: show ? width : 0,
  minWidth: show ? width : 0,
  maxWidth: show ? width : 0,
  height: '100%',
  overflow: 'hidden',
  transition: theme.transitions.create(['width', 'min-width', 'max-width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  border: 'none',
  backgroundColor: theme.palette.background.paper,
  boxShadow: show ? '2px 0 4px rgba(0,0,0,0.05)' : 'none',
}));

const InboxPane = styled(Box)<{ width: number; show: boolean }>(({ width, show, theme }) => ({
  width: show ? width : 0,
  minWidth: show ? width : 0,
  maxWidth: show ? width : 0,
  height: '100%',
  overflow: 'hidden',
  transition: theme.transitions.create(['width', 'min-width', 'max-width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  border: 'none',
  // チャット一覧の背景色を少し薄くして階層感を演出
  backgroundColor: show ? theme.palette.grey[50] : theme.palette.background.paper,
}));

const ChatPane = styled(Box)(({ theme }) => ({
  flex: 1,
  height: '100%',
  overflow: 'hidden',
  backgroundColor: theme.palette.background.default,
  minWidth: 300, // チャットエリアの最小幅を小さく調整
  maxWidth: 'calc(100vw - 600px)', // 最大幅を制限してはみ出しを防ぐ
  position: 'relative',
  // メインチャットエリアの境界線を削除
  border: 'none',
}));

const ProfilePane = styled(Box)<{ width: number; show: boolean }>(({ width, show, theme }) => ({
  width: show ? width : 0,
  minWidth: show ? width : 0,
  maxWidth: show ? width : 0,
  height: '100%',
  overflow: 'hidden',
  transition: theme.transitions.create(['width', 'min-width', 'max-width'], {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen,
  }),
  border: 'none',
  boxShadow: show ? '-2px 0 4px rgba(0,0,0,0.05)' : 'none',
  // 顧客プロフィールエリアの背景色を少し変えて区別
  backgroundColor: show ? '#fafafa' : theme.palette.background.paper,
}));

export const FourPaneLayout: React.FC<FourPaneLayoutProps> = ({
  navigationPane,
  inboxPane,
  chatPane,
  profilePane,
  navigationWidth = 240,  // ナビゲーション幅を適正化
  inboxWidth = 300,       // チャット一覧幅を拡大
  profileWidth = 340,     // 顧客情報幅を拡大
}) => {
  const { layoutConfig } = useResponsiveLayout({
    defaultLeftWidth: inboxWidth,
    defaultRightWidth: profileWidth,
  });

  // レスポンシブ表示制御
  const showNavigation = layoutConfig.isDesktop;
  const showInbox = !layoutConfig.isMobile || layoutConfig.activePane === 'left';
  const showChat = !layoutConfig.isMobile || layoutConfig.activePane === 'center';
  const showProfile = layoutConfig.isDesktop || layoutConfig.activePane === 'right';

  return (
    <Container>
      {/* ナビゲーションペイン（左端）*/}
      <NavigationPane width={navigationWidth} show={showNavigation}>
        {navigationPane}
      </NavigationPane>

      {/* インボックスペイン（左中）*/}
      <InboxPane 
        width={layoutConfig.isMobile ? window.innerWidth : inboxWidth} 
        show={showInbox}
      >
        {inboxPane}
      </InboxPane>

      {/* チャットペイン（右中・メイン）*/}
      {showChat && (
        <ChatPane>
          {chatPane}
        </ChatPane>
      )}

      {/* プロファイルペイン（右端）*/}
      <ProfilePane 
        width={layoutConfig.isMobile ? window.innerWidth : profileWidth} 
        show={showProfile}
      >
        {profilePane}
      </ProfilePane>
    </Container>
  );
};

export default FourPaneLayout;
